import React from 'react';
import { useContext, useEffect, useRef, useState } from "react";
import invariant from 'tiny-invariant';
import { bindAll } from 'bind-event-listener';

import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { unsafeOverflowAutoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/unsafe-overflow/element';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
const blockBoardPanningAttr = 'data-block-board-panning';

import { Column } from './Column.jsx';
import {
	isCardData,
	isCardDropTargetData,
	isColumnData,
	isDraggingACard,
	isDraggingAColumn,
} from './Data.jsx';
import { SettingsContext } from './SettingsContext.js';
import { useSave } from '../../context/SaveContext.jsx';
import { useTranslation } from "react-i18next";
import { Copy, Ellipsis, Plus } from 'lucide-react';

export const Board = ({addColumn, addCard, deleteColumn, copyColumn, pasteColumn, cardSelector}) =>
{
	const {t} = useTranslation();
	const { buildDeck, setBuildDeck } = useSave();
	const [data, setData] = [buildDeck, setBuildDeck];

	const scrollableRef = useRef(null);
	const { settings } = useContext(SettingsContext);

	useEffect(() => {
		const element = scrollableRef.current;
		invariant(element);

		return combine(
			monitorForElements({
				canMonitor: isDraggingACard,
				onDrop({ source, location }) {
					const dragging = source.data;
					if (!isCardData(dragging)) {
						return;
					}

					const innerMost = location.current.dropTargets[0];

					if (!innerMost) {
						return;
					}
					const dropTargetData = innerMost.data;
					const homeColumnIndex = data.columns.findIndex(
						(column) => column.id === dragging.columnId,
					);
					
					const home = data.columns[homeColumnIndex];
					
					if (!home) {
						return;
					}
					const cardIndexInHome = home.cards.findIndex((card) => card.id === dragging.card.id);

					// dropping on a card
					if (isCardDropTargetData(dropTargetData)) {
						const destinationColumnIndex = data.columns.findIndex(
							(column) => column.id === dropTargetData.columnId,
						);
						const destination = data.columns[destinationColumnIndex];

						// reordering in home column
						if (home === destination) {
							const cardFinishIndex = home.cards.findIndex(
								(card) => card.id === dropTargetData.card.id,
							);

							// could not find cards needed
							if (cardIndexInHome === -1 || cardFinishIndex === -1) {
								return;
							}

							// no change needed
							if (cardIndexInHome === cardFinishIndex) {
								return;
							}

							const closestEdge = extractClosestEdge(dropTargetData);

							const reordered = reorderWithEdge({
								axis: 'vertical',
								list: home.cards,
								startIndex: cardIndexInHome,
								indexOfTarget: cardFinishIndex,
								closestEdgeOfTarget: closestEdge,
							});

							const updated = {
								...home,
								cards: reordered
							};
							const columns = Array.from(data.columns);
							columns[homeColumnIndex] = updated;
							setData({ ...data, columns });
							return;
						}

						// moving card from one column to another

						// unable to find destination
						if (!destination) {
							return;
						}

						const indexOfTarget = destination.cards.findIndex(
							(card) => card.id === dropTargetData.card.id,
						);

						const closestEdge = extractClosestEdge(dropTargetData);
						const finalIndex = closestEdge === 'bottom' ? indexOfTarget + 1 : indexOfTarget;

						// remove card from home list
						const homeCards = Array.from(home.cards);
						homeCards.splice(cardIndexInHome, 1);

						// insert into destination list
						const destinationCards = Array.from(destination.cards);
						destinationCards.splice(finalIndex, 0, dragging.card);

						const columns = Array.from(data.columns);
						columns[homeColumnIndex] = {
							...home,
							cards: homeCards,
						};
						columns[destinationColumnIndex] = {
							...destination,
							cards: destinationCards,
						};
						setData({ ...data, columns });
						return;
					}

					// dropping onto a column, but not onto a card
					if (isColumnData(dropTargetData)) {
						const destinationColumnIndex = data.columns.findIndex(
							(column) => column.id === dropTargetData.column.id,
						);
						const destination = data.columns[destinationColumnIndex];

						if (!destination) {
							return;
						}

						// dropping on home
						if (home === destination) {
							// move to last position
							const reordered = reorder({
								list: home.cards,
								startIndex: cardIndexInHome,
								finishIndex: home.cards.length - 1,
							});

							const updated = {
								...home,
								cards: reordered,
							};
							const columns = Array.from(data.columns);
							columns[homeColumnIndex] = updated;
							setData({ ...data, columns });
							return;
						}

						// remove card from home list

						const homeCards = Array.from(home.cards);
						homeCards.splice(cardIndexInHome, 1);

						// insert into destination list
						const destinationCards = Array.from(destination.cards);
						destinationCards.splice(destination.cards.length, 0, dragging.card);

						const columns = Array.from(data.columns);
						columns[homeColumnIndex] = {
							...home,
							cards: homeCards
						};
						columns[destinationColumnIndex] = {
							...destination,
							cards: destinationCards
						}
						setData({ ...data, columns });
						return;
					}
				}
			}),
			monitorForElements({
				canMonitor: isDraggingAColumn,
				onDrop({ source, location }) {
					const dragging = source.data;
					if (!isColumnData(dragging)) {
						return;
					}

					const innerMost = location.current.dropTargets[0];
					if (!innerMost) {
						return;
					}
					const dropTargetData = innerMost.data;

					if (!isColumnData(dropTargetData)) {
						return;
					}

					const homeIndex = data.columns.findIndex((column) => column.id === dragging.column.id);
					const destinationIndex = data.columns.findIndex(
						(column) => column.id === dropTargetData.column.id,
					);

					if (homeIndex === -1 || destinationIndex === -1) {
						return;
					}

					if (homeIndex === destinationIndex) {
						return;
					}

					const reordered = reorder({
						list: data.columns,
						startIndex: homeIndex,
						finishIndex: destinationIndex,
					});
					setData({ ...data, columns: reordered });
				}
			}),
			autoScrollForElements({
				canScroll({ source }) {
					if (!settings.isOverElementAutoScrollEnabled) {
						return false;
					}
					return isDraggingACard({ source }) || isDraggingAColumn({ source });
				},
				getConfiguration: () => ({ maxScrollSpeed: settings.boardScrollSpeed }),
				element,
			}),
			unsafeOverflowAutoScrollForElements({
				element,
				getConfiguration: () => ({ maxScrollSpeed: settings.boardScrollSpeed }),
				canScroll({ source }) {
					if (!settings.isOverElementAutoScrollEnabled) {
						return false;
					}
					if (!settings.isOverflowScrollingEnabled) {
						return false;
					}
					return isDraggingACard({ source }) || isDraggingAColumn({ source });
				},
				getOverflow() {
					return {
						forLeftEdge: {
							top: 1000,
							left: 1000,
							bottom: 1000,
						},
						forRightEdge: {
							top: 1000,
							right: 1000,
							bottom: 1000,
						},	
					}
				}
			})
		);
	} , [data, settings]);

	useEffect(() => {
		let cleanupActive = null;
		const scrollable = scrollableRef.current;
		invariant(scrollable);

		function begin({ startX }) {
			let lastX = startX;

			const cleanupEvents = bindAll(
				window,
				[
					{
						type: 'pointermove',
						listener(event) {
							const currentX = event.clientX;
							const diffX = lastX - currentX;

							lastX = currentX;
							scrollable?.scrollBy({left: diffX});
						}
					},
					// stop panning if we see any of these events
					...(
						[
							'pointercancel',
							'pointerup',
							'pointerdown',
							'keydown',
							'resize',
							'click',
							'visibilitychange',
						]
					).map((eventName) => ({ type: eventName, listener: () => cleanupEvents() })),
				],
				{ capture: true },
			);

			cleanupActive = cleanupEvents;
		}

		const cleanupStart = bindAll(scrollable, [
			{
				/*
				type: 'pointerdown',
				listener(event) {
					if (!(event.target)) {
						return;
					}
					// ignore interactive elements
					if (event.target.closest(`[${blockBoardPanningAttr}]`)) {
						return;
					}

					begin({ startX: event.clientX });
				}
				*/
			}
		]);

		return function cleanupAll() {
			cleanupStart();
			cleanupActive?.();
		};
	}, []);

	return (
		<div
			className={`h-100 d-flex flex-column ${settings.isBoardMoreObvious ? 'px-5 py-5' : ''}`}
		>
			<div
				className={`h-100 d-flex flex-row align-items-start gap-1 overflow-x-auto ${settings.isBoardMoreObvious ? 'rounded border border-dashed' : ''}`}
				ref={scrollableRef}
			>
				{data.columns.map((column, index) => (
					<Column
						key={index} column={column} index={index}
						addCard={addCard}
						deleteColumn={deleteColumn}
						copyColumn={copyColumn}
						pasteColumn={pasteColumn}
						cardSelector={cardSelector}
					/>
				))}
				{/* Add New Group */}
				<div className="card"
					onClick={addColumn}
					style={{
							minWidth: "200px",
							borderStyle: "dashed",
							cursor: "pointer",
					}}
				>
					<div	
						className="card-body d-flex align-items-center justify-content-center"
					>
						<Plus />
						<strong>{t("AddGroup")}</strong>
					</div>
				</div>
			</div>
		</div>
	);
};
